// frontend/src/pages/Register.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Collapse,
  Link as MuiLink,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Check as CheckIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import TermsGatedGoogleDoor from "../components/auth/TermsGatedGoogleDoor";
import { playTermsNudge } from "../components/auth/termsNudge";
import FriendCodeSignupField from "../components/friendCode/FriendCodeSignupField";
import useSignupDoors, {
  DOOR_FORM,
  DOOR_GOOGLE,
  SIGNUP_SURFACE_REGISTER,
} from "../hooks/useSignupDoors";
import { getSignupFormCopy, SIGNUP_LEGAL_HREFS } from "../content/signupCopy";
// The public site's pill nav (owner round 2, "every public route"): it
// renders its own top spacer off "/", which is what clears the fixed bar.
import HomeNav from "../components/homepage/HomeNav";
import Seo from "../components/Seo";
import { colors, typography } from "../theme/tokens";
import { getMotionState } from "../motion/motionSignal";
import useHydrated from "../motion/useHydrated";
import useSiteIsHebrew from "../hooks/useSiteIsHebrew";
import useAdoptPrehydrationValue from "../hooks/useAdoptPrehydrationValue";
import useDiscardPrehydrationCheck from "../hooks/useDiscardPrehydrationCheck";
import {
  isPasswordValid,
  checkPasswordRequirements,
  calculatePasswordStrength,
} from "../utils/passwordRules";

// The sign-up BEHAVIOUR (terms gate, attribution, friend code, registration
// proof, Google, analytics) lives in hooks/useSignupDoors.js and the gated
// Google door in components/auth/TermsGatedGoogleDoor.js - shared with the
// conversion popup, so the two surfaces can never sign users up differently.
// This page owns only its layout and its labels.

// The disabled "create account" button keeps its 45% look (owner, audit round
// 2 option A) - the real button, not yet, never a grey slab. The Google door no
// longer dims (D16): it looks active and a tap explains the gate instead.
const DISABLED_SUBMIT_OPACITY = 0.45;
const SUBMIT_GRADIENT = `linear-gradient(135deg, ${colors.primary.main} 0%, #00A896 100%)`;

// Floating gradient orb — matches Login decorative layer
const GradientOrb = ({ sx }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: "absolute",
      borderRadius: "50%",
      pointerEvents: "none",
      ...sx,
    }}
  />
);

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  // Follows the site language the visitor picked on the public pages, and the
  // toggle here writes it back so the rest of the site follows in turn. Hebrew
  // on the hydration pass of the prerendered snapshot (which is Hebrew), the
  // stored choice from the next render on - see hooks/useSiteIsHebrew.js.
  const [isHebrew, setSiteIsHebrew] = useSiteIsHebrew();
  const toggleLanguage = () => setSiteIsHebrew(!isHebrew);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  // The card's entrance fade (opacity 0 -> 1 below). Only for a client-only
  // boot: when React is HYDRATING the prerendered snapshot, the card is
  // already on screen, fully visible, and must stay that way - so `mounted`
  // starts true there, which is also exactly what the snapshot holds.
  const hydrated = useHydrated();
  const [mounted, setMounted] = useState(!hydrated);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const {
    agreeToTerms,
    setAgreeToTerms,
    friendCode,
    submitEmail,
    onGoogleSuccess,
    onGoogleError,
    loading,
    googleLoading,
    busy,
    error,
    success,
    markRegistrationStarted,
    trackPasswordStrength,
  } = useSignupDoors({ surface: SIGNUP_SURFACE_REGISTER, isHebrew });

  // A Google error renders under the Google door; bring a fresh one into view
  // ("nearest" = no jump when it is already visible). Instant, not smooth, so
  // it needs no reduced-motion branch.
  const googleErrorRef = useRef(null);
  useEffect(() => {
    if (error.door === DOOR_GOOGLE && error.message) {
      googleErrorRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [error]);

  // A tap on the unticked Google door: the door reveals its hint next to the
  // button, and the page draws the eye to the one thing that unlocks it. The
  // terms panel sits BELOW the Google button now (right above "create
  // account"), so it is highlighted, scrolled to the middle of the screen and
  // its checkbox focused and given a short shake (every tap; none when motion
  // is off - termsNudge.js). The highlight stays until the box is ticked.
  // Smooth unless the visitor asked for no motion (the one motion switch).
  // `preventScroll` on the focus: a plain focus() jumps the page at once and
  // would cut the smooth scroll short.
  const [termsAttention, setTermsAttention] = useState(false);
  const termsPanelRef = useRef(null);
  const termsInputRef = useRef(null);
  const termsNudgeRef = useRef(null);
  const handleRequireTerms = useCallback(() => {
    setTermsAttention(true);
    termsPanelRef.current?.scrollIntoView?.({
      block: "center",
      behavior: getMotionState().off ? "auto" : "smooth",
    });
    termsInputRef.current?.focus?.({ preventScroll: true });
    playTermsNudge(termsNudgeRef.current);
  }, []);
  const handleTermsChange = (e) => {
    setAgreeToTerms(e.target.checked);
    if (e.target.checked) setTermsAttention(false);
  };
  // A tick the prerendered box got before React attached - a tap, or the
  // browser's own form restoration re-ticking it - is NOT consent: the DOM is
  // set back to the gate's state on hydration, and the visitor ticks it again
  // with React attached. See hooks/useDiscardPrehydrationCheck.js.
  useDiscardPrehydrationCheck(termsInputRef, agreeToTerms);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  // Translations
  const text = useMemo(() => ({
    // The copy shared with the conversion popup (terms text, field labels,
    // requirement chips, the divider, the Google hint) - content/signupCopy.js.
    ...getSignupFormCopy(isHebrew),
    headingCard: isHebrew ? "צור חשבון" : "Create your account",
    subtitle: isHebrew
      ? "14 ימי ניסיון חינם, ללא צורך בכרטיס אשראי"
      : "14-day free trial, no credit card required",
    passwordStrength: isHebrew ? "חוזק סיסמה" : "Password strength",
    weak: isHebrew ? "חלש" : "Weak",
    fair: isHebrew ? "בינוני" : "Fair",
    good: isHebrew ? "טוב" : "Good",
    strong: isHebrew ? "חזק" : "Strong",
    signUp: isHebrew ? "צור חשבון" : "Create Account",
    alreadyHaveAccount: isHebrew ? "כבר יש לך חשבון?" : "Already have an account?",
    signIn: isHebrew ? "התחבר" : "Sign in",
    security: isHebrew
      ? "מאובטח עם הצפנת SSL של 256 סיביות"
      : "Secured with 256-bit SSL encryption",
    formLabel: isHebrew ? "טופס הרשמה" : "Registration form",
    // The agree / success / failure messages are content/signupCopy.js
    // getSignupMessages(), shown by hooks/useSignupDoors.js.
    // Tab title + <html lang> (backlog #449), set through <Seo> like Forgot
    // password. The Hebrew half is public/index.html's default <title> - the
    // one this page has always carried - and must stay byte-identical to it
    // (authPagesSiteLanguage*.test.js read index.html); English translates it.
    seoTitle: isHebrew
      ? "Leadify AI - לידים והזדמנויות עסקיות בזמן אמת מקבוצות פייסבוק"
      : "Leadify AI - Real-time leads and business opportunities from Facebook groups",
  }), [isHebrew]);

  const passwordStrength = calculatePasswordStrength(formData.password);

  /**
   * One field's new value, exactly as its onChange delivers it - except that
   * an adoption (below) passes `track: false` and reports one commit later.
   */
  const applyFieldValue = useCallback(
    (name, value, { track = true } = {}) => {
      setFormData((prev) => ({ ...prev, [name]: value }));

      if (name === "password") {
        if (track) trackPasswordStrength(value);
        setShowPasswordRequirements(Boolean(value));
      }
    },
    [trackPasswordStrength]
  );

  const handleChange = (e) => applyFieldValue(e.target.name, e.target.value);

  // What the visitor typed (or the browser autofilled) into the prerendered
  // snapshot before React attached - kept, not wiped by the first render, and
  // handled as the onChange React missed would have handled it (the password
  // requirements open). The field's first focus happened then too, so it
  // counts as the start of the registration. See
  // hooks/useAdoptPrehydrationValue.js. Never logged: one is a password.
  //
  // The ANALYTICS half of an adoption (`registration_started`, the password
  // strength bucket) is NOT sent from the adoption itself, which runs in a
  // layout effect of the commit that hydrates this page. The consent gate
  // opens in AnalyticsProvider's PASSIVE mount effect (analytics.initialize()).
  // Today the two are in different commits - the page sits in App.js's route
  // <Suspense>, whose marked snapshot React hydrates in a pass after the root
  // commit - but should the page ever hydrate in the provider's own commit,
  // the layout effect would run first: the gate would drop the event while
  // markRegistrationStarted() burned its once-per-mount latch, and the field's
  // later onFocus could never send it. So the adoption only records WHAT to
  // report, and the passive effect below reports it: its state update commits
  // in a later render, and React flushes every pending passive effect
  // (initialize() included) before rendering one. The consent gate itself is
  // unchanged - trackEvent applies it, at a moment it is ready to.
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const [adoptionToReport, setAdoptionToReport] = useState(null);
  const adoptEmail = useCallback(
    (value) => {
      applyFieldValue("email", value, { track: false });
      setAdoptionToReport((prev) => ({ password: false, ...prev }));
    },
    [applyFieldValue]
  );
  const adoptPassword = useCallback(
    (value) => {
      applyFieldValue("password", value, { track: false });
      setAdoptionToReport((prev) => ({ ...prev, password: true }));
    },
    [applyFieldValue]
  );
  useAdoptPrehydrationValue(emailInputRef, formData.email, adoptEmail);
  useAdoptPrehydrationValue(passwordInputRef, formData.password, adoptPassword);

  // Each report object is sent once (the ref), whatever else re-runs this.
  const reportedAdoptionRef = useRef(null);
  useEffect(() => {
    if (!adoptionToReport || reportedAdoptionRef.current === adoptionToReport) return;
    reportedAdoptionRef.current = adoptionToReport;
    markRegistrationStarted();
    // The password state this report was committed with - the adopted one.
    if (adoptionToReport.password) trackPasswordStrength(formData.password);
  }, [adoptionToReport, formData.password, markRegistrationStarted, trackPasswordStrength]);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitEmail(formData);
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 25) return "#f44336";
    if (passwordStrength < 50) return "#ff9800";
    if (passwordStrength < 75) return "#ffc107";
    return "#4caf50";
  };

  const getPasswordStrengthLabel = () => {
    if (passwordStrength < 20) return text.weak;
    if (passwordStrength < 40) return text.fair;
    if (passwordStrength < 80) return text.good;
    return text.strong;
  };

  // Shared label sx — identical pattern to Login, handles RTL positioning.
  //
  // Every input root below is `direction: ltr`, so MUI cuts the outline's
  // notch (the gap the floating label sits in) at the LEFT edge in both
  // languages. The shrunk label therefore goes LEFT in both languages too -
  // which Hebrew always did; the English branch used to send it right, away
  // from its notch, so the border ran through an empty gap on one side and
  // straight behind the label on the other. The resting English label starts
  // at the field's own edge (MUI's 14px), not 20px further in.
  const labelSx = useMemo(() => ({
    "& .MuiInputLabel-root": {
      fontSize: "15px",
      color: "rgba(0, 0, 0, 0.55)",
      [isHebrew ? "right" : "left"]: isHebrew ? 20 : 0,
      [isHebrew ? "left" : "right"]: "auto",
      transformOrigin: isHebrew ? "top right" : "top left",
      backgroundColor: "transparent",
      paddingLeft: "4px",
      paddingRight: "4px",
      "&:not(.MuiInputLabel-shrink)": {
        transform: isHebrew ? "translate(0, 13px) scale(1)" : "translate(14px, 13px) scale(1)",
        "@media (max-width: 600px)": {
          transform: isHebrew ? "translate(0, 11px) scale(1)" : "translate(14px, 11px) scale(1)",
        },
      },
      "&.MuiInputLabel-shrink": {
        left: 14,
        right: "auto",
        top: "-8px",
        transform: "translate(0, 0) scale(0.75)",
        transformOrigin: "top left",
        backgroundColor: "white",
      },
      // In Hebrew: hide MUI's appended asterisk (appears on the wrong side in RTL)
      // and add it via ::before so it appears on the right (RTL logical start)
      ...(isHebrew ? {
        "& .MuiFormLabel-asterisk": { display: "none" },
        "&.Mui-required::before": {
          content: '"* "',
          color: "#d32f2f",
          fontSize: "inherit",
        },
      } : {}),
    },
    "& .MuiOutlinedInput-root": {
      fontSize: "15px",
      backgroundColor: "rgba(255,255,255,0.75)",
      borderRadius: "12px",
      transition: "all 0.2s ease",
      "& fieldset": {
        borderColor: colors.primary.alpha18,
        transition: "border-color 0.2s ease",
      },
      "&:hover fieldset": {
        borderColor: colors.primary.alpha40,
      },
      "&.Mui-focused fieldset": {
        borderColor: colors.primary.main,
        borderWidth: "1.5px",
      },
      "&.Mui-focused": {
        backgroundColor: "#ffffff",
        boxShadow: `0 0 0 3px ${colors.primary.alpha12}`,
      },
      "& input": { padding: "10px 14px" },
      "@media (max-width: 600px)": {
        "& input": { padding: "8px 12px" },
      },
    },
  }), [isHebrew]);

  const isSubmitDisabled = busy || !agreeToTerms || !isPasswordValid(formData.password);

  // The error alert, rendered at `door`'s slot only when that door raised the
  // current error - so exactly one alert is mounted at a time and its
  // role="alert" fires once. `spacingSx` is the slot's own margin.
  const renderErrorAlert = (door, spacingSx) => {
    if (!error.message || error.door !== door) return null;
    return (
      <Alert
        ref={door === DOOR_GOOGLE ? googleErrorRef : undefined}
        severity="error"
        role="alert"
        aria-live="assertive"
        sx={{
          ...spacingSx,
          borderRadius: "12px",
          fontSize: "13px",
          textAlign: isHebrew ? "right" : "left",
          // MUI's default icon margin sits on the physical right,
          // leaving no gap between icon and text in RTL. Apply
          // explicit margins per language direction.
          "& .MuiAlert-icon": {
            marginLeft: isHebrew ? "12px" : 0,
            marginRight: isHebrew ? 0 : "12px",
          },
        }}
      >
        {error.message}
      </Alert>
    );
  };

  return (
    <Box
      dir={isHebrew ? "rtl" : "ltr"}
      sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}
    >
      <Seo title={text.seoTitle} path="/register" lang={isHebrew ? "he" : "en"} />
      {/* ── Background decorative layer — mirrors Login ── */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          background: "linear-gradient(150deg, #EDFAF8 0%, #F8FDFF 45%, #FDF3F7 100%)",
        }}
      >
        <GradientOrb sx={{
          top: "-8%", right: "-10%",
          width: { xs: "60vw", sm: "50vw" },
          height: { xs: "60vw", sm: "50vw" },
          maxWidth: 520, maxHeight: 520,
          background: `radial-gradient(circle, ${colors.primary.alpha18} 0%, transparent 65%)`,
          filter: "blur(70px)",
        }} />
        <GradientOrb sx={{
          bottom: "-14%", left: "-10%",
          width: { xs: "65vw", sm: "55vw" },
          height: { xs: "65vw", sm: "55vw" },
          maxWidth: 600, maxHeight: 600,
          background: `radial-gradient(circle, ${colors.secondary.alpha20} 0%, transparent 65%)`,
          filter: "blur(75px)",
        }} />
        <Box sx={{
          position: "absolute", inset: 0,
          backgroundImage: `radial-gradient(circle, ${colors.primary.alpha10} 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
          maskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 20%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 50% 50%, black 20%, transparent 100%)",
          opacity: { xs: 0.6, sm: 1 },
        }} />
      </Box>

      {/* ── Public pill nav (HomeNav renders the spacer that clears it) ── */}
      <HomeNav isHebrew={isHebrew} toggleLanguage={toggleLanguage} />

      {/* ── Centred content (below the fixed pill + its spacer) ── */}
      <Box sx={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        pt: 0, pb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 }, position: "relative", zIndex: 1,
      }}>

        {/* ── Main card wrapper ── */}
        <Box
          sx={{
            width: "100%", maxWidth: 420,
            opacity: mounted ? 1 : 0,
            transform: mounted ? "translateY(0)" : "translateY(18px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          {/* Glass card */}
          <Box sx={{
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: `1px solid ${colors.primary.alpha18}`,
            borderRadius: "24px",
            boxShadow: `0 8px 40px ${colors.primary.alpha08}, 0 2px 12px rgba(0,0,0,0.05)`,
            p: { xs: 3, sm: 4.5 },
          }}>
            {/* Card header */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontFamily: typography.fontFamily.heading,
                  fontSize: { xs: "22px", sm: "26px" },
                  color: "#2D3748",
                  lineHeight: 1.2,
                  mb: 0.75,
                  letterSpacing: "-0.01em",
                }}
              >
                {text.headingCard}
              </Typography>
              <Typography sx={{
                color: colors.primary.dark,
                fontSize: "13px",
                fontFamily: typography.fontFamily.body,
                fontWeight: 500,
                letterSpacing: "0.01em",
              }}>
                {text.subtitle}
              </Typography>
            </Box>

            {/* Google SSO - FIRST of the two doors (D16). The gate is the shared
                door: `inert` wrapper + `disabled` button + a sibling
                tap-catcher that explains it, plus the hook's re-check in
                onGoogleSuccess. Outside the form, so nothing in the Google
                subtree can ever submit it. */}
            <Box>
              <TermsGatedGoogleDoor
                agreed={agreeToTerms}
                onRequireTerms={handleRequireTerms}
                hint={text.googleTermsHint}
                onSuccess={onGoogleSuccess}
                onError={onGoogleError}
                loading={googleLoading || loading}
                isHebrew={isHebrew}
                /* Copy only - picks the "registration with email and password"
                   wording if the Google script fails to load. The auth call's
                   own mode is set in the hook. */
                mode="register"
              />
              {/* Errors raised by the Google door land here, next to the
                  button that raised them (only one alert is ever mounted, so it
                  is announced once). */}
              {renderErrorAlert(DOOR_GOOGLE, { mt: 0.5 })}
            </Box>

            {/* Divider between the two sign-up doors */}
            <Box sx={{ my: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.08)" }} />
              <Typography sx={{
                fontSize: "12px", color: "#94A3B8", fontWeight: 500,
                fontFamily: typography.fontFamily.body,
                letterSpacing: "0.05em",
                userSelect: "none",
                textTransform: "uppercase",
              }}>
                {text.or}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(0,0,0,0.08)" }} />
            </Box>

            {/* Alerts - an email-form error opens the form; a Google error is
                rendered under the Google door instead (see DOOR_GOOGLE). */}
            {renderErrorAlert(DOOR_FORM, { mb: 2.5 })}
            {success && (
              <Alert
                severity="success"
                role="status"
                aria-live="polite"
                sx={{
                  mb: 2.5, borderRadius: "12px",
                  backgroundColor: colors.primary.alpha08,
                  color: "#00A896",
                  border: `1px solid ${colors.primary.alpha20}`,
                  fontSize: "13px",
                  textAlign: isHebrew ? "right" : "left",
                  "& .MuiAlert-icon": {
                    color: colors.primary.main,
                    marginLeft: isHebrew ? "12px" : 0,
                    marginRight: isHebrew ? 0 : "12px",
                  },
                }}
              >
                {success}
              </Alert>
            )}

            {/* Form */}
            {/* method="post" (#464): the prerendered form is on screen before
                React attaches, and a native submit there (Enter in a field) is
                a GET by default - the email and password would land in the
                address bar, the history, the request logs and analytics' page
                URL. The NATIVE-SUBMIT GUARD head script in public/index.html
                stops a native submit before hydration; this is the layer
                under it: as a POST, nothing goes in the URL. No
                `action` on purpose: the default (this page's own static URL)
                is harmless, while naming an endpoint would invite a real
                request outside the sign-up flow and its terms gate. Once React
                is attached, handleSubmit preventDefaults every submit. */}
            <Box
              component="form"
              method="post"
              onSubmit={handleSubmit}
              noValidate
              aria-label={text.formLabel}
            >
              {/* Email */}
              <TextField
                margin="none"
                required
                fullWidth
                id="email"
                label={text.email}
                name="email"
                type="email"
                autoComplete="email"
                /* No autoFocus: Google comes first now (D16), and on a phone
                   a focused email field pops the keyboard over it. */
                inputRef={emailInputRef}
                value={formData.email}
                onChange={handleChange}
                onFocus={markRegistrationStarted}
                inputProps={{ "aria-label": text.email, "aria-required": true }}
                sx={{
                  mb: 2,
                  ...labelSx,
                  "& .MuiOutlinedInput-root": {
                    ...labelSx["& .MuiOutlinedInput-root"],
                    direction: "ltr",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingRight: isHebrew ? "16px" : "14px",
                    paddingLeft: isHebrew ? "14px" : "16px",
                  },
                }}
              />

              {/* Password */}
              <TextField
                margin="none"
                required
                fullWidth
                name="password"
                label={text.password}
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="new-password"
                inputRef={passwordInputRef}
                value={formData.password}
                onChange={handleChange}
                inputProps={{ "aria-label": text.password, "aria-required": true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        position: "absolute",
                        [isHebrew ? "left" : "right"]: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    >
                      <IconButton
                        aria-label={showPassword ? text.hidePassword : text.showPassword}
                        onClick={handleClickShowPassword}
                        edge="end"
                        size="small"
                        sx={{ color: "#94A3B8", "&:hover": { color: colors.primary.main }, transition: "color 0.2s ease" }}
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1.25,
                  ...labelSx,
                  "& .MuiOutlinedInput-root": {
                    ...labelSx["& .MuiOutlinedInput-root"],
                    direction: "ltr",
                    paddingLeft: isHebrew ? "44px" : "inherit",
                    paddingRight: isHebrew ? "inherit" : "44px",
                  },
                  "& .MuiOutlinedInput-input": {
                    paddingRight: isHebrew ? "16px" : "44px",
                    paddingLeft: isHebrew ? "44px" : "16px",
                  },
                }}
              />

              {/* Compact password requirements — inline chip row with live met/unmet state */}
              <Collapse in={showPasswordRequirements}>
                <Box
                  role="list"
                  aria-label={text.requirementsLabel}
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    justifyContent: isHebrew ? "flex-end" : "flex-start",
                  }}
                >
                  {checkPasswordRequirements(formData.password, text.passwordRequirementLabels).map((req) => (
                    <Box
                      key={req.id}
                      role="listitem"
                      aria-label={`${req.full} - ${req.met ? text.requirementMet : text.requirementMissing}`}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        px: 1,
                        py: 0.4,
                        borderRadius: "8px",
                        fontSize: "11px",
                        fontWeight: 600,
                        fontFamily: typography.fontFamily.body,
                        letterSpacing: "0.02em",
                        background: req.met ? colors.primary.alpha12 : "rgba(0,0,0,0.035)",
                        color: req.met ? colors.primary.dark : "#94A3B8",
                        border: `1px solid ${req.met ? colors.primary.alpha30 : "rgba(0,0,0,0.06)"}`,
                        transition: "all 0.25s ease",
                      }}
                    >
                      {req.met && <CheckIcon sx={{ fontSize: 12 }} />}
                      <span>{req.short}</span>
                    </Box>
                  ))}
                </Box>
              </Collapse>

              {/* Password Strength Bar */}
              {formData.password && (
                <Box sx={{ mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography sx={{
                      fontSize: "11.5px",
                      color: "#94A3B8",
                      fontFamily: typography.fontFamily.body,
                    }}>
                      {text.passwordStrength}
                    </Typography>
                    <Typography sx={{
                      fontSize: "11.5px",
                      fontWeight: 600,
                      color: getPasswordStrengthColor(),
                      fontFamily: typography.fontFamily.body,
                    }}>
                      {getPasswordStrengthLabel()}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    sx={{
                      height: 4, borderRadius: 3,
                      backgroundColor: "rgba(0,0,0,0.06)",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: getPasswordStrengthColor(),
                        borderRadius: 3,
                        transition: "background-color 0.3s ease",
                      },
                    }}
                    aria-label={`${text.passwordStrength}: ${getPasswordStrengthLabel()}`}
                  />
                </Box>
              )}

              {/* Friend code - after the password, right before the terms box
                  and the submit (the conversion popup's order). It still
                  applies to BOTH doors: the code lives in the useSignupDoors
                  state, not in this <form>, so a code typed here rides the
                  Google request as well. Collapsed to one line unless a
                  `?ref=` link pre-filled it. Its toggle is type="button", so
                  opening it never submits the form. */}
              <FriendCodeSignupField
                {...friendCode.fieldProps}
                isHebrew={isHebrew}
                labelSx={labelSx}
              />

              {/* Terms consent - directly ABOVE "create account" (owner: where
                  every sign-up page puts it). It still gates BOTH doors: the
                  submit (isSubmitDisabled + the hook's own re-check) and the
                  Google door above (a tap on it while unticked scrolls here -
                  handleRequireTerms). Inside the <form> because it is part of
                  what the visitor submits; it has no `name`, the submit reads
                  the hook's state, never the form's fields, and Enter in the
                  email/password fields still submits exactly as before. The
                  whole panel is the <label>, which is what gives the small box
                  its >= 44px touch target. */}
              <FormControlLabel
                ref={termsPanelRef}
                control={
                  // The shake (handleRequireTerms) plays on this plain span,
                  // not on the MUI root: React rewrites the root's class when
                  // its state classes change (focus-visible, checked), which
                  // would drop the shake class mid-run; this span has none.
                  <span ref={termsNudgeRef} style={{ display: "inline-flex", flex: "none" }}>
                    <Checkbox
                      checked={agreeToTerms}
                      onChange={handleTermsChange}
                      inputRef={termsInputRef}
                      size="small"
                      sx={{
                        color: colors.primary.alpha40,
                        "&.Mui-checked": { color: colors.primary.main },
                        padding: "6px",
                      }}
                      inputProps={{ "aria-label": text.terms }}
                    />
                  </span>
                }
                label={
                  <Typography sx={{
                    fontSize: "13px",
                    color: "#64748B",
                    fontFamily: typography.fontFamily.body,
                    lineHeight: 1.5,
                  }}>
                    {text.terms}{" "}
                    <MuiLink href={SIGNUP_LEGAL_HREFS.termsOfService} target="_blank" rel="noopener noreferrer"
                      sx={{ color: colors.primary.dark, textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                      {text.termsOfService}
                    </MuiLink>
                    {text.comma}{" "}
                    <MuiLink href={SIGNUP_LEGAL_HREFS.privacyPolicy} target="_blank" rel="noopener noreferrer"
                      sx={{ color: colors.primary.dark, textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                      {text.privacyPolicy}
                    </MuiLink>{" "}
                    {text.andThe}{" "}
                    <MuiLink href={SIGNUP_LEGAL_HREFS.cookiePolicy} target="_blank" rel="noopener noreferrer"
                      sx={{ color: colors.primary.dark, textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                      {text.cookiePolicy}
                    </MuiLink>
                  </Typography>
                }
                sx={{
                  // MUI's own label margins are physical (-11px left / 16px
                  // right) and this app has no RTL theme - zero them so the
                  // panel sits flush in both directions.
                  m: 0,
                  mb: 1.5,
                  width: "100%",
                  boxSizing: "border-box",
                  minHeight: 44,
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 1.25,
                  border: "1px solid rgba(14, 22, 40, 0.10)",
                  borderRadius: "12px",
                  backgroundColor: "#F7F9FA",
                  transition: "border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                  "html.lf-motion-off &": { transition: "none" },
                  "&:focus-within": { borderColor: colors.primary.main },
                  // Raised by a tap on the unticked Google door, cleared by
                  // ticking: the panel the user has to act on lights up.
                  ...(termsAttention && !agreeToTerms
                    ? {
                        borderColor: colors.primary.main,
                        backgroundColor: colors.primary.alpha08,
                        boxShadow: `0 0 0 3px ${colors.primary.alpha20}`,
                      }
                    : {}),
                }}
              />

              {/* Submit CTA */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitDisabled}
                aria-label={text.signUp}
                sx={{
                  mt: 1,
                  py: 1.75,
                  fontSize: "15px",
                  fontWeight: 600,
                  fontFamily: typography.fontFamily.body,
                  borderRadius: "25px",
                  textTransform: "none",
                  letterSpacing: "0.01em",
                  background: SUBMIT_GRADIENT,
                  color: "#ffffff",
                  boxShadow: "0 4px 20px rgba(0, 168, 150, 0.28)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #48D1CC 0%, #00B5A2 100%)",
                    boxShadow: "0 6px 24px rgba(0, 168, 150, 0.38)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": { transform: "translateY(0)" },
                  "&:focus-visible": { outline: `2px solid ${colors.primary.main}`, outlineOffset: 2 },
                  // Disabled = the SAME teal button at 45% (owner, audit
                  // round 2 option A), not a grey slab - it reads as "this is
                  // the button, not yet". Keyed on MUI's own state class so it
                  // can never drift from the real `disabled` attribute.
                  "&.Mui-disabled": {
                    background: SUBMIT_GRADIENT,
                    color: "#ffffff",
                    opacity: DISABLED_SUBMIT_OPACITY,
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                ) : (
                  text.signUp
                )}
              </Button>
            </Box>

            {/* Sign-in prompt */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography
                component="span"
                sx={{
                  fontSize: "13.5px",
                  color: "#94A3B8",
                  fontFamily: typography.fontFamily.body,
                }}
              >
                {text.alreadyHaveAccount}{" "}
                <Link to="/login" style={{ textDecoration: "none" }}>
                  <Typography component="span" sx={{
                    fontSize: "13.5px",
                    color: colors.primary.dark,
                    fontWeight: 600,
                    fontFamily: typography.fontFamily.body,
                    transition: "color 0.2s ease",
                    "&:hover": { color: colors.primary.main },
                    cursor: "pointer",
                  }}>
                    {text.signIn}
                  </Typography>
                </Link>
              </Typography>
            </Box>

            {/* Security badge */}
            <Box sx={{
              mt: 3, pt: 2.5,
              borderTop: `1px solid ${colors.primary.alpha10}`,
              display: "flex", alignItems: "center",
              justifyContent: "center", gap: 0.75,
            }}>
              <LockIcon sx={{ fontSize: "14px", color: "#B0C4CD" }} />
              <Typography sx={{
                fontSize: "12px",
                color: "#B0C4CD",
                fontFamily: typography.fontFamily.body,
                fontWeight: 500,
              }}>
                {text.security}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
